import { ReactComponent as FeatureBedroomSvg } from 'assets/svg/feature-bedroom.svg';
import { ReactComponent as FeatureBathroomSvg } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as FeatureBuildAreaSvg } from 'assets/svg/feature-buildarea.svg';
import { ReactComponent as FeaturePlotSizeSvg } from 'assets/svg/feature-plotsize.svg';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatNumber } from 'util/Funcs';
import { PropertyDTO } from 'util/Types';
import { Divider } from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import { PropertyAdditionalDetail } from 'types/Property';
import PropertyActionButton from './PropertyActionButton';
import Skeleton from 'react-loading-skeleton';
import { useModals } from 'pages/MainProvider';

interface DetailPaneProps {
    property: PropertyDTO | null;
    additionalDetails: PropertyAdditionalDetail | null;
    onExplorerListingClicked?: () => void;
    isDetailLoading?: boolean;
    isPublic?: boolean;
    onClose?: () => void;
}

const DetailPane = (props: DetailPaneProps) => {
    const { additionalDetails, property, isDetailLoading = true, isPublic = false, onClose } = props;
    const { isCompetitorPreviewOpened } = useModals();

    if (!property?.details) return null;

    const details = property.details;

    const renderAvailability = (status: string | undefined) => {
        if (!status) return null;
        const lowerStatus = status.toLowerCase();

        let bgColor = 'bg-green-100';
        let textColor = 'text-green-600';

        if (lowerStatus === 'sold' || lowerStatus === 'rented') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-600';
        } else if (lowerStatus === 'reserved') {
            bgColor = 'bg-yellow-100';
            textColor = 'text-yellow-600';
        } else if (lowerStatus === 'available') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-600';
        }

        return <div className={`px-[12px] py-[4px] rounded-[4px] text-[14px] font-medium ${bgColor} ${textColor}`}>{status}</div>;
    };

    return (
        <div className="flex flex-col gap-[12px] w-full">
            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-[4px]">
                    <div className="text-[24px] font-bold text-my-black">
                        {isDetailLoading ? (
                            <Skeleton width={200} />
                        ) : property?.details?.price && property.details.price > 0 ? (
                            `€ ${formatNumber(property.details.price)}`
                        ) : (
                            'On Request'
                        )}
                    </div>
                    {property?.details?.pricePerSquareMeter && property.details.pricePerSquareMeter > 0 && (
                        <div className="text-[16px] text-my-black text-opacity-80">{formatNumber(Math.round(property.details.pricePerSquareMeter))} €/m²</div>
                    )}
                </div>
                {renderAvailability(property?.details?.availability)}
            </div>
            <Divider className="w-full" />
            <div className="flex justify-between items-center">
                <div className="relative flex flex-wrap gap-[10px] text-[16px]">
                    {details.bedrooms > 0 && (
                        <>
                            <div className="flex items-center gap-[8px]">
                                <FeatureBedroomSvg />

                                <span className="text-my-black">
                                    <span className="font-bold">{formatNumber(details.bedrooms)}</span>
                                </span>
                            </div>
                            <Divider className="h-full" orientation="vertical" />
                        </>
                    )}
                    {details.bathrooms > 0 && (
                        <>
                            <div className="flex items-center gap-[8px]">
                                <FeatureBathroomSvg />

                                <span className="text-my-black">
                                    <span className="font-bold">{formatNumber(details.bathrooms)}</span>
                                </span>
                            </div>
                            <Divider className="h-full" orientation="vertical" />
                        </>
                    )}

                    {details.sizeLiving > 0 && (
                        <>
                            <div className="flex items-center gap-[8px]">
                                <FeatureBuildAreaSvg />

                                <span className="text-my-black">
                                    <span className="font-bold">{`${formatNumber(details.sizeLiving)} m²`}</span>
                                    <span className="text-opacity-80">{` living size`}</span>
                                </span>
                            </div>
                            <Divider className="h-full" orientation="vertical" />
                        </>
                    )}
                    {details.sizePlot > 0 && (
                        <div className="flex items-center gap-[8px]">
                            <FeaturePlotSizeSvg />

                            <span className="text-my-black">
                                <span className="font-bold">{`${formatNumber(details.sizePlot)} m²`}</span>
                                <span className="text-opacity-80">{` plot area`}</span>
                            </span>
                        </div>
                    )}
                </div>
                {!isCompetitorPreviewOpened && !isPublic && (
                    <div className="xl:block hidden">
                        {isDetailLoading ? (
                            <Skeleton height={32} width={80} />
                        ) : (
                            <PropertyActionButton property={property} additionalDetails={additionalDetails} onClose={onClose} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetailPane;
